import { Check, Target, Zap, Award, ShieldCheck, Sparkles, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function MissionPricingSection() {
  const missionPillars = [
    {
      icon: Target,
      title: 'Exam-Pattern Practice',
      description: 'Structured mock tests with 20-minute sectional timers, practice questions, and authentic marking schemes.',
    },
    {
      icon: Zap,
      title: '4-Method Solutions',
      description: 'Master questions via 4 lenses: First-Principles, 10s Speed Shortcuts, Case Checks, and Visual Logic.',
    },
    {
      icon: Award,
      title: 'Pay-Per-Test Access',
      description: 'Full-length high-yield mock tests with percentile analytics, accuracy breakdown, and solutions starting at just ₹9.',
    },
  ]

  const planFeatures = [
    'Access 1 Full-Length High-Yield Mock Test',
    '4-Method Step-by-Step Solutions & Quick Formulas',
    'Real-time Percentile Ranking & Accuracy Breakdown',
    'Speed & Time Analytics per Question Category',
    'Detailed Topic-Wise Weak Area Diagnostics',
  ]

  return (
    <section id="pricing" className="w-full py-20 sm:py-28 bg-white border-t border-slate-100">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

          {/* Left Column: Inspiring Mission Statement & Core Pillars */}
          <div className="lg:col-span-7 space-y-8 text-left">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f4f7fb] border border-slate-200 text-[#131b2e] text-xs font-bold tracking-wide uppercase">
                <Target className="w-3.5 h-3.5 text-[#c8933b]" />
                <span>Our Purpose & Commitment</span>
              </div>

              <h2 className="!font-sans text-3xl sm:text-4xl lg:text-[46px] font-black text-[#131b2e] tracking-tight leading-[1.12]">
                Empowering Every Aspirant to Turn Ambition into Rank
              </h2>

              <p className="!font-sans text-base sm:text-lg text-slate-600 leading-relaxed font-normal max-w-2xl">
                We believe competitive exam excellence should not be confined to elite coaching hubs. PrepKiya is built to level the playing field — delivering structured Bank PO & SBI Clerk practice tests, four-dimensional deep solutions, and actionable rank analytics to students across India.
              </p>
            </div>

            {/* Core Mission Pillars */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              {missionPillars.map((pillar, i) => {
                const IconComponent = pillar.icon
                return (
                  <div
                    key={i}
                    className="p-5 rounded-2xl bg-[#f8fafc] border border-slate-200 hover:border-[#131b2e] hover:shadow-md transition-all flex flex-col justify-between"
                  >
                    <div>
                      <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-[#131b2e] flex items-center justify-center mb-3 shadow-2xs">
                        <IconComponent className="w-5 h-5 text-[#c8933b]" />
                      </div>
                      <h4 className="!font-sans font-bold text-sm text-[#131b2e] mb-1.5">
                        {pillar.title}
                      </h4>
                      <p className="!font-sans text-xs text-slate-600 leading-relaxed">
                        {pillar.description}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Right Column: Pay-Per-Test Plan Card matching Landing Visual Palette */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[440px] pt-4">

              {/* Lucrative Floating Top Ribbon Badge */}
              <div className="absolute top-0 right-6 z-20">
                <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#c8933b] text-white text-xs font-black tracking-wider uppercase shadow-md border-2 border-white">
                  <Award className="w-3.5 h-3.5 fill-white text-white" />
                  <span>Most Popular • Best Value</span>
                </div>
              </div>

              {/* Card Container */}
              <div className="w-full bg-white border-2 border-[#131b2e]/10 rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all relative">

                {/* Dark Navy Card Header with Gold Avatar */}
                <div className="bg-[#131b2e] text-white px-7 py-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#c8933b] animate-pulse" />
                    <h3 className="!font-sans font-bold text-lg text-white tracking-wide">
                      Pay-Per-Test Plan
                    </h3>
                  </div>
                  <span className="!font-sans text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/10 text-slate-200 border border-white/10">
                    No Subscription Trap
                  </span>
                </div>

                {/* Card Body */}
                <div className="p-7 sm:p-8 space-y-6">

                  {/* Price */}
                  <div className="space-y-1.5 pb-4 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <span className="!font-sans text-xs font-bold text-slate-500 uppercase tracking-wider block">
                        One-Time Full Access
                      </span>
                      <span className="!font-sans text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-md">
                        Save 60% vs. Subscriptions
                      </span>
                    </div>

                    <div className="flex items-baseline gap-2.5 pt-1">
                      <span className="!font-sans text-5xl font-black text-[#c8933b]">
                        ₹49
                      </span>
                      <span className="!font-sans text-base font-semibold text-slate-400 line-through">
                        ₹799
                      </span>
                      <span className="!font-sans text-sm font-medium text-slate-500">
                        / Full-Length Test
                      </span>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-3.5">
                    <h4 className="!font-sans text-xs font-bold text-[#131b2e] uppercase tracking-wider">
                      Everything Included in Every Test:
                    </h4>
                    <ul className="space-y-3">
                      {planFeatures.map((feat, i) => (
                        <li key={i} className="!font-sans flex items-start gap-3 text-sm text-slate-700 font-medium">
                          <div className="mt-0.5 w-4 h-4 rounded-full bg-[#f4f7fb] border border-slate-200 text-[#131b2e] flex items-center justify-center shrink-0">
                            <Check className="w-3 h-3 text-[#131b2e] stroke-[3]" />
                          </div>
                          <span className="leading-snug">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Purchase Test CTA */}
                  <div className="pt-2 space-y-3">
                    <Button
                      asChild
                      className="!font-sans w-full bg-[#c8933b] hover:bg-[#b5802a] text-white font-bold h-12 rounded-xl shadow-md text-base transition-all group"
                    >
                      <a href="/users/sign_up" className="flex items-center justify-center gap-2">
                        <span>Get Instant Access</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </a>
                    </Button>

                    <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                      <ShieldCheck className="w-4 h-4 text-[#c8933b]" />
                      <span>Instant evaluation</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  )
}
